import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { menuInfo } from "@shared/type/CommonType";
import { getInitFormData } from "@shared/utils/CommonUtils";
import { fnGetDataSet } from "@shared/api/restApi";
import SearchPanel, { ValueChangedEvent } from "@widgets/SearchPanel";
import SnGrid from "@shared/components/GridV2/SnGrid";
import SnTab from "@shared/components/SnTab";
import { SnGridRef } from "@shared/components/GridV2/SnGridTypes";

interface Props {
  Menuprops: menuInfo;
}

interface TabState {
  page: string;
  searchParams: any | null;
}

const SN_SAL_STX_REP_024 = forwardRef(({ Menuprops }: Props, ref) => {
  const searchRef = useRef<any>(null);
  const gridRefs = useRef<{ [key: string]: React.RefObject<SnGridRef> }>({
    grid1: React.createRef<SnGridRef>(),
    grid2: React.createRef<SnGridRef>(),
    grid3: React.createRef<SnGridRef>(),
    grid4: React.createRef<SnGridRef>(),
  });
  const getGridCurrent = (key: string): SnGridRef => {
    const grid = gridRefs.current[key]?.current;
    if (!grid) {
      throw new Error(`Grid '${key}' is not mounted yet`);
    }
    return grid;
  };
  const [formData, setFormData] = useState<any>();
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const [columns, setColums] = useState<any>({
    grid1: [],
    grid2: [],
    grid3: [],
    grid4: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
    grid2: [],
    grid3: [],
    grid4: [],
  });
  const [tabPageState, setTabState] = useState<TabState>({
    page: "1",
    searchParams: null,
  });

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormControlData(rtn.formControlData);
        setFormData(rtn.formData);
        setColums({
          grid1: rtn.grid.grid1,
          grid2: rtn.grid.grid2,
          grid3: rtn.grid.grid3,
          grid4: rtn.grid.grid4,
        });
      } catch (error) {}
    };
    fetchform();
  }, []);

  useImperativeHandle(ref, () => ({
    onSearch,
  }));

  const onSearch = async (searchData: any) => {
    try {
      const searchDataToUse = searchData || searchRef.current.getFormdata();
      searchDataToUse.yy = searchDataToUse.yy.slice(0, 4);
      if (tabPageState.page === "1") {
        const params = {
          id: "sn_sal_stx_rep_024_01",
          params: searchDataToUse,
        };

        getGridCurrent("grid1").resetUpdate();
        const data: any = await fnGetDataSet(params);

        setDataSources({
          grid1: data[0],
          grid2: [],
          grid3: [],
          grid4: [],
        });
      } else if (tabPageState.page === "2") {
        const params = {
          id: "sn_sal_stx_rep_024_02",
          params: searchDataToUse,
        };
        getGridCurrent("grid2").resetUpdate();
        const data: any = await fnGetDataSet(params);

        setDataSources({
          grid1: [],
          grid2: data[0],
          grid3: [],
          grid4: [],
        });
      } else if (tabPageState.page === "3") {
        const params = {
          id: "sn_sal_stx_rep_024_03",
          params: searchDataToUse,
        };

        getGridCurrent("grid3").resetUpdate();
        const data: any = await fnGetDataSet(params);

        setDataSources({
          grid1: [],
          grid2: [],
          grid3: data[0],
          grid4: [],
        });
      } else if (tabPageState.page === "4") {
        const params = {
          id: "sn_sal_stx_rep_024_04",
          params: searchDataToUse,
        };

        getGridCurrent("grid4").resetUpdate();
        const data: any = await fnGetDataSet(params);

        setDataSources({
          grid1: [],
          grid2: [],
          grid3: [],
          grid4: data[0],
        });
      }
    } catch (error: any) {}
  };

  const onTabChanged = async (index: string) => {
    setTabState({ page: index, searchParams: null });
  };

  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
        events: {},
      },
      grid2: {
        columns: columns.grid2,
        dataSource: dataSources.grid2,
        events: {},
      },
      grid3: {
        columns: columns.grid3,
        dataSource: dataSources.grid3,
        events: {},
      },
      grid4: {
        columns: columns.grid4,
        dataSource: dataSources.grid4,
        events: {},
      },
    }),
    [columns, dataSources]
  );

  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
        ></SearchPanel>
      </div>

      <div className="flex-1 flex flex-col gap-1.5 min-h-0">
        <SnTab
          titles={["고객별", "품목별", "담당자별", "수주형태별"]}
          width={"100%"}
          selectedTab={tabPageState.page}
          onTabChanged={onTabChanged}
          body={[
            <SnGrid
              ref={gridRefs.current["grid1"]}
              columnConfig={grids.grid1.columns}
              rowData={grids.grid1.dataSource}
              etcProps={{ rowSelection: "multiple" }}
              events={grids.grid1.events}
            />,
            <SnGrid
              ref={gridRefs.current["grid2"]}
              columnConfig={grids.grid2.columns}
              rowData={grids.grid2.dataSource}
              etcProps={{ rowSelection: "multiple" }}
              events={grids.grid2.events}
            />,

            <SnGrid
              ref={gridRefs.current["grid3"]}
              columnConfig={grids.grid3.columns}
              rowData={grids.grid3.dataSource}
              etcProps={{ rowSelection: "multiple" }}
              events={grids.grid3.events}
            />,

            <SnGrid
              ref={gridRefs.current["grid4"]}
              columnConfig={grids.grid4.columns}
              rowData={grids.grid4.dataSource}
              etcProps={{ rowSelection: "multiple" }}
              events={grids.grid4.events}
            />,
          ]}
        />
      </div>
    </div>
  );
});

export default SN_SAL_STX_REP_024;
